import { createStyles, WithStyles, withStyles } from "@material-ui/core/styles";
import classnames from "classnames";
import * as React from "react";

import { Link, RelodeTheme, Text } from "../";
import {
    FileAudioIcon,
    FileImageIcon,
    FileTextIcon,
    FileVideoIcon,
    FileWordIcon,
    LinkIcon,
} from "../../assets/icons";

const styles = (theme: RelodeTheme) =>
    createStyles({
        root: {},
        wrapper: {
            display: "inline-flex",
            alignItems: "center",
        },
        linkTextPrimaryColor: {
            fontSize: "16px",
            color: theme.palette.primary.main,
        },
        icon: {
            marginRight: theme.spacing(1),
        },
        linkInfo: {
            flexGrow: 1,
        },
        showMedia: {
            display: "block",
            maxWidth: 480,
        },
        hideMedia: {
            display: "none",
        },
        date: {
            textAlign: "right",
            [theme.breakpoints.down("md")]: {
                display: "none",
            },
        },
        attachmentSize: {
            textAlign: "right",
            [theme.breakpoints.down("md")]: {
                display: "none",
            },
        },
        linkText: {
            maxWidth: 400,
            textOverflow: "ellipsis",
            overflow: "hidden",
            wordBreak: "break-word",
        },
    });

interface AttachmentLinkProps extends WithStyles<typeof styles> {
    baseId: string;
    className?: string;
    date?: any;
    icon?: boolean;
    label: string;
    style?: object;
    url: string;
    type?: string;
    playMediaInNewWindow?: boolean;
    highlightType?: "external" | "internal";
    hideHighlight?: boolean;
    gutterBottom?: boolean;
    usePrimaryColorLink?: boolean;
    friendlyAttachmentSize?: string;
}

interface AttachmentLinkState {
    showAudioPlayer: boolean;
    showVideoPlayer: boolean;
}

export const AttachmentLink = withStyles(styles, { name: "AttachmentLink" })(
    class extends React.Component<AttachmentLinkProps, AttachmentLinkState> {
        private videoRef: any = null;
        private audioRef: any = null;

        constructor(props) {
            super(props);

            this.state = {
                showAudioPlayer: false,
                showVideoPlayer: false,
            };
        }

        clickAudio = () => {
            this.setState(prevState => {
                if (prevState.showAudioPlayer) this.audioRef.pause();
                else this.audioRef.play();
                return {
                    ...prevState,
                    showAudioPlayer: !prevState.showAudioPlayer,
                };
            });
        };

        clickVideo = () => {
            this.setState(prevState => {
                if (prevState.showVideoPlayer) this.videoRef.pause();
                else this.videoRef.play();
                return {
                    ...prevState,
                    showVideoPlayer: !prevState.showVideoPlayer,
                };
            });
        };

        render() {
            const {
                baseId,
                classes,
                className: classNameProp,
                date,
                gutterBottom,
                hideHighlight,
                highlightType = "external",
                icon: iconProp,
                label,
                playMediaInNewWindow,
                style,
                url,
                usePrimaryColorLink = false,
                friendlyAttachmentSize,
            } = this.props;

            const extendedId = `${baseId}AttachmentLink`;

            const { showAudioPlayer, showVideoPlayer } = this.state;

            let icon: JSX.Element | string = "";
            let type = "link";

            const audioClass = classnames({
                [classes.showMedia]: showAudioPlayer,
                [classes.hideMedia]: !showAudioPlayer,
            });

            const videoClass = classnames({
                [classes.showMedia]: showVideoPlayer,
                [classes.hideMedia]: !showVideoPlayer,
            });

            const linkTextClass = classnames(classes.linkText, {
                [classes.linkTextPrimaryColor]: usePrimaryColorLink,
            });

            switch (this.props.type) {
                case "text/plain":
                    icon = (
                        <FileTextIcon
                            className={classes.icon}
                            color="primary"
                        />
                    );
                    break;
                case "application/pdf":
                    icon = (
                        <FileImageIcon
                            className={classes.icon}
                            color="primary"
                        />
                    );
                    break;
                case "web":
                    icon = (
                        <LinkIcon className={classes.icon} color="primary" />
                    );
                    break;
                case "audio/mpeg":
                case "audio/mp3":
                case "audio/ogg":
                case "application/ogg":
                case "audio/x-wav":
                case "audio/wav":
                case "audio/m4a":
                case "audio/x-m4a":
                    icon = (
                        <FileAudioIcon
                            className={classes.icon}
                            color="primary"
                        />
                    );
                    type = "audio";
                    break;
                case "video/mp4":
                case "video/webm":
                case "video/ogg":
                    icon = (
                        <FileVideoIcon
                            className={classes.icon}
                            color="primary"
                        />
                    );
                    type = "video";
                    break;
                case "application/msword":
                case "application/vnd.ms-word.document.macroenabled.12":
                case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                case "text/rtf":
                    icon = (
                        <FileWordIcon
                            className={classes.icon}
                            color="primary"
                        />
                    );
                    break;
                default:
                    icon = (
                        <FileTextIcon
                            className={classes.icon}
                            color="primary"
                        />
                    );
            }

            return (
                <div className={classes.root} id={`${extendedId}Wrapper`}>
                    <div className={classes.wrapper} style={style}>
                        <div className={classes.linkInfo}>
                            <div style={{ verticalAlign: "middle" }}>
                                {type === "link" && (
                                    <Link
                                        href={url}
                                        className={classNameProp}
                                        id={`${extendedId}Link`}
                                        target="_blank"
                                        title={label}
                                        highlightType={highlightType}
                                        hideHighlight={hideHighlight}
                                        gutterBottom={gutterBottom}
                                    >
                                        {iconProp ? icon : null}
                                        <Text
                                            variant="body1"
                                            isInline={true}
                                            className={linkTextClass}
                                        >
                                            {label}
                                        </Text>
                                    </Link>
                                )}
                                {type === "audio" && (
                                    <Link
                                        id={`${extendedId}Link`}
                                        href={
                                            playMediaInNewWindow
                                                ? url
                                                : undefined
                                        }
                                        onClick={
                                            !playMediaInNewWindow
                                                ? this.clickAudio
                                                : undefined
                                        }
                                        target="_blank"
                                        title={`${label} ${type} ${date} ${friendlyAttachmentSize}`}
                                        highlightType={highlightType}
                                        hideHighlight={hideHighlight}
                                        gutterBottom={gutterBottom}
                                    >
                                        {iconProp ? icon : null}
                                        <Text
                                            variant="body1"
                                            isInline={true}
                                            className={linkTextClass}
                                        >
                                            {label}
                                        </Text>
                                    </Link>
                                )}
                                {type === "video" && (
                                    <Link
                                        id={`${extendedId}Link`}
                                        href={
                                            playMediaInNewWindow
                                                ? url
                                                : undefined
                                        }
                                        onClick={
                                            !playMediaInNewWindow
                                                ? this.clickVideo
                                                : undefined
                                        }
                                        target="_blank"
                                        title={`${label} ${type} ${date} ${friendlyAttachmentSize}`}
                                        highlightType={highlightType}
                                        hideHighlight={hideHighlight}
                                        gutterBottom={gutterBottom}
                                    >
                                        {iconProp ? icon : null}
                                        <Text
                                            variant="body1"
                                            isInline={true}
                                            className={linkTextClass}
                                        >
                                            {label}
                                        </Text>
                                    </Link>
                                )}
                                {date && (
                                    <Text
                                        variant="caption"
                                        className={classes.date}
                                    >
                                        {"  \u2022 "}
                                        {date}
                                    </Text>
                                )}
                                {friendlyAttachmentSize ? (
                                    <Text
                                        variant="caption"
                                        className={classes.attachmentSize}
                                    >
                                        {"  \u2022 "}
                                        {friendlyAttachmentSize}
                                    </Text>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    {type === "audio" && (
                        <div>
                            <audio
                                controls={true}
                                className={audioClass}
                                ref={ref => (this.audioRef = ref)}
                            >
                                <source src={url} type={type} />
                                <a href={url} target="_blank">
                                    <Text
                                        variant="body1"
                                        isInline={true}
                                        className={linkTextClass}
                                    >
                                        click to open
                                    </Text>
                                </a>
                            </audio>
                        </div>
                    )}
                    {type === "video" && (
                        <div>
                            <video
                                controls={true}
                                className={videoClass}
                                ref={ref => (this.videoRef = ref)}
                            >
                                <source src={url} type={type} />
                                <a href={url} target="_blank">
                                    <Text
                                        variant="body1"
                                        isInline={true}
                                        className={linkTextClass}
                                    >
                                        click to open
                                    </Text>
                                </a>
                            </video>
                        </div>
                    )}
                </div>
            );
        }
    }
);
